import { logger } from "../lib/logger";
import { DooDatesError, ErrorFactory } from "../lib/error-handling";
import { supabase } from "../lib/supabase";
import { SupportTicketNotificationService } from "./SupportTicketNotificationService";
import { getSupportProduct } from "./supportProduct";

/** Ligne `product_subscriptions` telle que la requête support la ramène. */
interface SupportSubscriptionRow {
  product: string;
  plan_type: string | null;
  lemonsqueezy_status: string | null;
  lemonsqueezy_current_period_end: string | null;
  lemonsqueezy_cancel_at_period_end: boolean | null;
  trial_ends_at: string | null;
  trial_used_at: string | null;
}

export interface CustomerInfo {
  planType: string | null; // 'free' | 'solo' | 'starter_*' | 'pro' | 'ltd_*' | null
  subscriptionStatus: string | null; // 'active' | 'past_due' | 'cancelled' | 'trialing' | 'paused' | null
  periodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /** Fin d'essai Solo, ou null. Un « solo » avec cette date est un ESSAI. */
  trialEndsAt: string | null;
  /** Essai en cours au moment de la lecture. */
  trialActive: boolean;
  /** Un essai a déjà été consommé, en cours ou terminé. */
  hasUsedTrial: boolean;
}

export interface SupportTicket {
  id: string;
  email: string;
  subject: string;
  status: "open" | "in_progress" | "resolved" | "closed";
  priority?: number; // 0=low, 1=normal, 2=high, 3=critical
  severity?: string; // "low" | "normal" | "high" | "critical" | "blocker"
  type?: string; // cf. support_tickets_type_check : bug, question, feature, feedback, other + creation, participation, export, integration, account, billing + abuse (signalement d'un sondage)
  metadata?: any;
  created_at: string;
  updated_at: string;
  // Champs d'assignation
  assigned_to?: string; // UUID du profil assigné
  assigned_at?: string; // Timestamp de l'assignation
  assigned_by?: string; // UUID de qui a assigné
  assigned_email?: string; // Email de la personne assignée (pour l'affichage)
  is_assigned?: boolean; // Helper pour l'affichage
  // Info client (jointure profiles par email)
  customer?: CustomerInfo | null;
}

export interface SupportMessage {
  id: string;
  ticket_id: string;
  sender_type: "user" | "agent" | "ia" | "system";
  sender_email: string;
  content: string;
  content_type: "text" | "html";
  metadata?: any;
  content_attributes?: {
    draft_author?: string;
    draft_kind?: "fix" | "info-request";
    pr_url?: string;
    [key: string]: any;
  } | null;
  status: "draft" | "sent";
  created_at: string;
}

export interface CreateTicketParams {
  email: string;
  subject: string;
  content: string;
  priority?: number;
  severity?: string;
  type?: string;
  metadata?: any;
  /** Sondage concerné (colonne `support_tickets.poll_id`, ex. signalement d'abus). */
  pollId?: string;
  /**
   * Insère avec la session de l'utilisateur connecté (user_id = auth.uid()),
   * au lieu de la clé anon. Le ticket n'est alors lisible que par son auteur
   * et les admins (policy SELECT), et la limite « 10 / utilisateur / heure »
   * s'applique au lieu de « 3 / e-mail / 10 min ». Sans session : repli anon.
   */
  asCurrentUser?: boolean;
}

export class SupportTicketService {
  private static instance: SupportTicketService;
  private supabase;

  private constructor() {
    this.supabase = supabase;
  }

  public static getInstance(): SupportTicketService {
    if (!SupportTicketService.instance) {
      SupportTicketService.instance = new SupportTicketService();
    }
    return SupportTicketService.instance;
  }

  /**
   * Liste tous les tickets (réservé aux admins)
   */
  async listTickets(
    options: {
      status?: string[];
      type?: string[];
      limit?: number;
      offset?: number;
    } = {},
  ): Promise<SupportTicket[]> {
    try {
      // Use the supabase-js client so the user's session JWT is sent with
      // the request. The view runs with security_invoker=on, which means
      // RLS on support_tickets is evaluated as the caller — admin reads
      // only succeed when is_admin() / the JWT-based admin policies apply.
      // Safety net : la requête supabase-js peut hang indéfiniment (même
      // pattern que le fix sur getFeatureRequests). On race contre un
      // timeout 10s pour ne jamais laisser /admin en spinner infini.
      const queryPromise = this.supabase
        .from("support_tickets_with_assignment")
        .select("*")
        .order("created_at", { ascending: false });
      const { data, error } = (await Promise.race([
        queryPromise,
        new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error("listTickets timeout after 10s")),
            10000,
          ),
        ),
      ])) as Awaited<typeof queryPromise>;

      if (error) {
        throw new Error(`${error.code ?? "SUPABASE_ERROR"}: ${error.message}`);
      }

      // Apply filters client-side since the view returns all tickets
      let filteredData = data || [];

      if (options.status && options.status.length > 0) {
        filteredData = filteredData.filter((t: any) =>
          options.status!.includes(t.status),
        );
      }

      if (options.type && options.type.length > 0) {
        filteredData = filteredData.filter(
          (t: any) => t.type && options.type!.includes(t.type),
        );
      }

      if (options.limit) {
        filteredData = filteredData.slice(0, options.limit);
      }

      if (options.offset) {
        const start = options.offset;
        const end = start + (options.limit || 10);
        filteredData = filteredData.slice(start, end);
      }

      // Enrichir avec les infos client (plan, subscription) par email
      await this.enrichTicketsWithCustomerInfo(filteredData);

      return filteredData;
    } catch (error) {
      console.error("🎫 [SupportTicketService] Failed to list tickets:", error);
      logger.error("Failed to list tickets", "support", error);
      throw ErrorFactory.network("Erreur lors de la récupération des tickets");
    }
  }

  /**
   * Récupère un ticket par son ID avec ses messages
   */
  async getTicketDetails(
    ticketId: string,
  ): Promise<{ ticket: SupportTicket; messages: SupportMessage[] }> {
    try {
      // Client authentifié : la RLS de `support_tickets` n'ouvre plus rien à
      // la clé anon depuis le 28/09/2026 (branche « user_id IS NULL »
      // retirée). Un `fetch` avec la clé anon ne rendrait plus aucune ligne.
      // La page publique de feedback passe par getFeedbackTicket().
      const { data: ticket, error: ticketError } = await this.supabase
        .from("support_tickets")
        .select("*")
        .eq("id", ticketId)
        .maybeSingle();

      if (ticketError) {
        throw new Error(ticketError.message);
      }

      if (!ticket) {
        throw new Error("Ticket not found");
      }

      // Enrichir avec les infos client
      await this.enrichTicketsWithCustomerInfo([ticket]);

      const { data: messages, error: messagesError } = await this.supabase
        .from("support_messages")
        .select("*")
        .eq("ticket_id", ticketId)
        .order("created_at", { ascending: true });

      if (messagesError) {
        throw new Error(messagesError.message);
      }

      return { ticket, messages: messages || [] };
    } catch (error) {
      console.error(
        "🎫 [SupportTicketService] Failed to get ticket details:",
        error,
      );
      logger.error(
        `Failed to get ticket details for ${ticketId}`,
        "support",
        error,
      );
      throw ErrorFactory.network(
        "Erreur lors de la récupération des détails du ticket",
      );
    }
  }

  /**
   * Contexte minimal d'un ticket pour la page publique
   * `/support/feedback/:ticketId`, ouverte depuis un e-mail, souvent sans
   * compte. Passe par une RPC qui ne rend que l'identifiant et le sujet : la
   * table elle-même n'est plus lisible sans session.
   */
  async getFeedbackTicket(
    ticketId: string,
  ): Promise<{ id: string; subject: string }> {
    const { data, error } = await this.supabase.rpc(
      "get_support_feedback_ticket",
      { p_ticket_id: ticketId },
    );

    if (error) {
      logger.error(
        `Failed to get feedback ticket ${ticketId}`,
        "support",
        error,
      );
      throw ErrorFactory.network(
        "Erreur lors de la récupération des détails du ticket",
      );
    }

    const row = Array.isArray(data) ? data[0] : data;
    if (!row) {
      throw ErrorFactory.network("Ticket not found");
    }
    return row as { id: string; subject: string };
  }

  /**
   * Met à jour le statut, la priorité ou l'assignation d'un ticket
   */
  async updateTicket(
    ticketId: string,
    updates: Partial<
      Pick<
        SupportTicket,
        | "status"
        | "priority"
        | "type"
        | "metadata"
        | "assigned_to"
        | "assigned_at"
        | "assigned_by"
      >
    >,
  ): Promise<SupportTicket> {
    try {
      // D'abord vérifier si le ticket existe et récupérer l'ancien statut
      const { data: existingTicket, error: checkError } = await this.supabase
        .from("support_tickets")
        .select("*")
        .eq("id", ticketId)
        .single();

      if (checkError || !existingTicket) {
        throw ErrorFactory.notFound(`Ticket ${ticketId} not found`);
      }

      const oldStatus = existingTicket.status;

      // Utiliser la fonction RPC pour contourner les problèmes RLS
      console.log("Using RPC function to update ticket (bypass RLS)...");
      const { data, error } = await this.supabase.rpc("update_ticket_status", {
        ticketid: ticketId,
        status: updates.status,
        assigned_to: updates.assigned_to,
        assigned_at: updates.assigned_at,
        assigned_by: updates.assigned_by,
      });

      if (error) {
        console.error("RPC update failed:", error);
        throw error;
      }

      // La fonction RPC retourne { success: true, ticket: SupportTicket }
      if (!data || !data.success) {
        throw new Error(data?.error || "RPC update failed");
      }

      const updatedTicket = data.ticket;

      // Envoyer la notification de statut si nécessaire (en arrière-plan)
      if (updates.status && updates.status !== oldStatus) {
        console.log("=== STATUS CHANGE DETECTED ===");
        console.log("Old status:", oldStatus);
        console.log("New status:", updates.status);

        // Envoyer la notification en arrière-plan sans bloquer
        this.sendStatusNotificationAsync(
          updatedTicket,
          oldStatus,
          updates.status,
        );
      }

      return updatedTicket;
    } catch (error) {
      logger.error(`Failed to update ticket ${ticketId}`, "support", {
        error,
        ticketId,
        updates,
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      throw ErrorFactory.network("Erreur lors de la mise à jour du ticket");
    }
  }

  /**
   * Envoie la notification de statut en arrière-plan (non bloquant)
   */
  private async sendStatusNotificationAsync(
    ticket: SupportTicket,
    oldStatus: string,
    newStatus: string,
  ): Promise<void> {
    try {
      console.log("Sending status notification for ticket:", ticket.id);

      const notificationService =
        SupportTicketNotificationService.getInstance();
      const result = await notificationService.notifyStatusChange(
        ticket,
        oldStatus,
        newStatus,
        ticket.assigned_email || undefined,
      );

      if (result.success) {
        console.log("Status notification sent successfully:", result.emailId);
      } else {
        console.warn("Status notification failed:", result.error);
      }
    } catch (error) {
      // Ne pas bloquer la mise à jour du ticket si la notification échoue
      console.error(
        "Failed to send status notification (non-blocking):",
        error,
      );
      logger.warn(
        "Status notification failed but ticket was updated successfully",
        "support",
        { ticketId: ticket.id, error },
      );
    }
  }

  /**
   * Supprime définitivement un ticket de la base de données
   */
  async deleteTicket(ticketId: string): Promise<void> {
    try {
      // Vérifier que le ticket existe
      const { data: existingTicket, error: checkError } = await this.supabase
        .from("support_tickets")
        .select("id, subject")
        .eq("id", ticketId)
        .single();

      if (checkError || !existingTicket) {
        throw new Error(`Ticket ${ticketId} not found`);
      }

      // Essayer la fonction RPC admin d'abord (bypass RLS)
      const { data: rpcResult, error: rpcError } = await this.supabase.rpc(
        "admin_delete_support_ticket",
        { ticket_id: ticketId },
      );

      if (rpcError) {
        logger.warn(
          `RPC admin_delete failed, falling back to direct delete: ${rpcError.message}`,
          "support",
        );

        // Fallback vers le client Supabase direct
        const { error } = await this.supabase
          .from("support_tickets")
          .delete()
          .eq("id", ticketId);

        if (error) throw error;
      }

      logger.info(
        `Ticket ${ticketId} deleted successfully via ${rpcError ? "direct" : "RPC admin"}`,
        "support",
      );
    } catch (error) {
      logger.error(`Failed to delete ticket ${ticketId}`, "support", {
        error,
        ticketId,
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      throw ErrorFactory.network("Erreur lors de la suppression du ticket");
    }
  }

  /**
   * Ajoute un message à un ticket
   */
  async addMessage(
    ticketId: string,
    content: string,
    senderType: "user" | "agent" | "system" = "agent",
    senderEmail?: string,
  ): Promise<SupportMessage> {
    try {
      console.log(
        "🎫 [SupportTicketService] addMessage called for ticket:",
        ticketId,
      );

      const messageData = {
        ticket_id: ticketId,
        sender_type: senderType,
        sender_email: senderEmail || "admin@doodates.com",
        content,
        content_type: "text",
        created_at: new Date().toISOString(),
      };

      // Client authentifié (session admin) : la clé anon ne peut plus écrire
      // ni relire `support_messages` depuis le 28/09/2026.
      const { data: message, error } = await this.supabase
        .from("support_messages")
        .insert(messageData)
        .select()
        .single();

      if (error) {
        console.error("🎫 [SupportTicketService] Add message error:", error);
        throw new Error(error.message);
      }

      console.log("🎫 [SupportTicketService] Message added successfully");

      return message;
    } catch (error) {
      console.error("🎫 [SupportTicketService] Failed to add message:", error);
      logger.error(
        `Failed to add message to ticket ${ticketId}`,
        "support",
        error,
      );
      throw ErrorFactory.network("Erreur lors de l'envoi du message");
    }
  }

  /**
   * Marque un message draft comme envoyé. Utilisé par l'UI admin quand
   * un brouillon IA (Claude triage) est validé et envoyé à l'utilisateur.
   * Utilise le client supabase authentifié — la policy RLS `admin_all_messages`
   * (is_admin()) autorise l'UPDATE.
   */
  async sendDraft(messageId: string): Promise<SupportMessage> {
    const { data, error } = await this.supabase
      .from("support_messages")
      .update({ status: "sent" })
      .eq("id", messageId)
      .select("*")
      .single();

    if (error) {
      throw new Error(error.message);
    }
    return data as SupportMessage;
  }

  /**
   * Édite le contenu d'un draft avant envoi.
   */
  async editDraft(messageId: string, content: string): Promise<SupportMessage> {
    const { data, error } = await this.supabase
      .from("support_messages")
      .update({ content })
      .eq("id", messageId)
      .eq("status", "draft")
      .select("*")
      .single();

    if (error) {
      throw new Error(error.message);
    }
    return data as SupportMessage;
  }

  /**
   * Rejette (supprime) un draft.
   */
  async rejectDraft(messageId: string): Promise<void> {
    const { error } = await this.supabase
      .from("support_messages")
      .delete()
      .eq("id", messageId)
      .eq("status", "draft");

    if (error) {
      throw new Error(error.message);
    }
  }

  /**
   * Crée un nouveau ticket support (utilisé par SentryTicketBridge et RecentProblemsDetector)
   */
  async createTicket(params: CreateTicketParams): Promise<SupportTicket> {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      let bearer = supabaseKey;
      let userId: string | null = null;
      if (params.asCurrentUser) {
        const { data } = await this.supabase.auth.getSession();
        if (data.session?.access_token && data.session.user?.id) {
          bearer = data.session.access_token;
          userId = data.session.user.id;
        }
      }

      const now = new Date().toISOString();
      // Identifiant généré ici : l'insertion se fait en `return=minimal`, qui
      // ne demande AUCUN droit de lecture sur la ligne créée. Avant, la
      // réponse 201 de PostgREST était vide et la méthode levait
      // « Empty response from server » après avoir inséré le ticket.
      // `return=minimal` est OBLIGATOIRE depuis #1342 (20260928170000) : anon
      // n'a plus de SELECT sur support_tickets, `return=representation`
      // ferait échouer l'insertion d'un invité en 42501.
      const id = crypto.randomUUID();
      const metadata = { ...(params.metadata ?? {}) };
      // Convention lue par TicketSlidePanel : `metadata.message`. Le champ
      // `content` n'était jusqu'ici envoyé nulle part.
      if (params.content && metadata.message === undefined) {
        metadata.message = params.content;
      }

      const body = {
        id,
        email: params.email,
        subject: params.subject,
        type: params.type ?? "bug",
        severity: params.severity ?? "normal",
        priority: params.priority ?? 1,
        status: "open",
        metadata,
        ...(params.pollId ? { poll_id: params.pollId } : {}),
        ...(userId ? { user_id: userId } : {}),
        created_at: now,
        updated_at: now,
      };

      const response = await fetch(`${supabaseUrl}/rest/v1/support_tickets`, {
        method: "POST",
        headers: {
          apikey: supabaseKey,
          Authorization: `Bearer ${bearer}`,
          "Content-Type": "application/json",
          Prefer: "return=minimal",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorText = await response.text();
        if (errorText.includes("rate_limit_exceeded")) {
          throw ErrorFactory.rateLimit(
            `rate_limit_exceeded: ${errorText}`,
            "Trop de tickets envoyés, réessayez plus tard",
          );
        }
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      return {
        ...body,
        user_id: userId,
        poll_id: params.pollId ?? null,
      } as unknown as SupportTicket;
    } catch (error) {
      console.error(
        "🎫 [SupportTicketService] Failed to create ticket:",
        error,
      );
      logger.error("Failed to create ticket", "support", error);
      if (error instanceof DooDatesError) throw error;
      throw ErrorFactory.network("Erreur lors de la création du ticket");
    }
  }

  /**
   * Récupère l'historique d'un utilisateur par email, incluant les infos client
   * (plan, statut d'abonnement LemonSqueezy).
   */
  async getUserHistory(email: string): Promise<{
    ticketsCount: number;
    customer: CustomerInfo | null;
  }> {
    try {
      // 1. Compter les tickets — client authentifié (session admin) : la clé
      // anon ne lit plus `support_tickets` depuis le 28/09/2026.
      const { count } = await this.supabase
        .from("support_tickets")
        .select("id", { count: "exact", head: true })
        .eq("email", email);

      // 2. Récupérer le profil (plan, subscription) par email
      const customer = await this.fetchCustomerInfoByEmail(email);

      return {
        ticketsCount: count ?? 0,
        customer,
      };
    } catch (error) {
      logger.warn(`Failed to get user history for ${email}`, "support", error);
      return { ticketsCount: 0, customer: null };
    }
  }

  /**
   * Récupère les infos client (plan + subscription) d'un email unique.
   * Retourne null si aucun profil n'est trouvé.
   */
  private async fetchCustomerInfoByEmail(
    email: string,
  ): Promise<CustomerInfo | null> {
    try {
      // Utilise le client Supabase authentifié pour bénéficier de la session
      // admin (et contourner les policies RLS qui bloqueraient une requête
      // anon sur `profiles`).
      // Correctif 15/09 — `profiles` n'est PAS la source de vérité du plan.
      // L'essai Solo et le split multi-produits vivent dans
      // `product_subscriptions` ; ni le démarrage d'essai ni sa fin ne
      // touchent `profiles`. Lire `profiles` seul montrait « free » à une
      // personne au jour 12 de son essai — c'est-à-dire l'explication de la
      // plupart des questions qu'elle peut poser. La divergence existe déjà
      // hors essai : un compte au moins a `profiles.plan_type='free'` et
      // `product_subscriptions.plan_type='solo'`.
      const { data, error } = await this.supabase
        .from("profiles")
        .select(
          "plan_type, lemonsqueezy_status, lemonsqueezy_current_period_end, lemonsqueezy_cancel_at_period_end, id, product_subscriptions(product, plan_type, lemonsqueezy_status, lemonsqueezy_current_period_end, lemonsqueezy_cancel_at_period_end, trial_ends_at, trial_used_at)",
        )
        .eq("email", email)
        .limit(1);
      if (error) {
        logger.warn(
          `Failed to fetch customer info for ${email}`,
          "support",
          error,
        );
        return null;
      }
      const profile = data?.[0];
      if (!profile) return null;

      // Produit du support (date / form / …), pour ne pas répondre avec le
      // plan d'un autre produit que celui sur lequel la personne écrit.
      const product = getSupportProduct();
      const rows = (
        profile as { product_subscriptions?: SupportSubscriptionRow[] }
      ).product_subscriptions;
      const sub = Array.isArray(rows)
        ? rows.find((r) => r.product === product)
        : undefined;

      const trialEndsAt = sub?.trial_ends_at ?? null;
      const trialActive =
        !!trialEndsAt && new Date(trialEndsAt).getTime() > Date.now();

      return {
        // `product_subscriptions` prime ; `profiles` ne sert plus que de repli
        // pour les comptes antérieurs au split.
        planType: sub?.plan_type ?? profile.plan_type ?? null,
        subscriptionStatus:
          sub?.lemonsqueezy_status ?? profile.lemonsqueezy_status ?? null,
        periodEnd:
          sub?.lemonsqueezy_current_period_end ??
          profile.lemonsqueezy_current_period_end ??
          null,
        cancelAtPeriodEnd: !!(
          sub?.lemonsqueezy_cancel_at_period_end ??
          profile.lemonsqueezy_cancel_at_period_end
        ),
        // Sans ces deux champs, le support voit « solo » et croit parler à un
        // client payant alors qu'il s'agit d'un essai qui se termine demain.
        trialEndsAt,
        trialActive,
        hasUsedTrial: !!sub?.trial_used_at,
      };
    } catch {
      return null;
    }
  }

  /**
   * Enrichit en place une liste de tickets avec leur info client (jointure
   * batch sur la table profiles via email=in.(...)). Tolérant aux erreurs :
   * en cas d'échec, les tickets restent sans info client.
   */
  private async enrichTicketsWithCustomerInfo(
    tickets: SupportTicket[],
  ): Promise<void> {
    if (!tickets || tickets.length === 0) return;
    try {
      const uniqueEmails = Array.from(
        new Set(tickets.map((t) => t.email).filter(Boolean)),
      );
      if (uniqueEmails.length === 0) return;

      // Client Supabase authentifié → propage la session admin pour lever
      // les policies RLS sur `profiles` (l'anon key ne peut rien lire).
      // Même correctif que fetchCustomerInfoByEmail : `profiles` n'est pas la
      // source de vérité du plan. Ce chemin-ci alimente la LISTE des tickets —
      // l'endroit où l'on trie et où l'on décide qui traiter en premier. Y
      // laisser « free » pour un essayeur au jour 12 fausse la priorisation
      // autant que la réponse.
      const { data: profiles, error } = await this.supabase
        .from("profiles")
        .select(
          "email, plan_type, lemonsqueezy_status, lemonsqueezy_current_period_end, lemonsqueezy_cancel_at_period_end, product_subscriptions(product, plan_type, lemonsqueezy_status, lemonsqueezy_current_period_end, lemonsqueezy_cancel_at_period_end, trial_ends_at, trial_used_at)",
        )
        .in("email", uniqueEmails);

      if (error || !profiles) {
        logger.warn(
          "Failed to enrich tickets with customer info",
          "support",
          error,
        );
        return;
      }

      const product = getSupportProduct();
      const byEmail = new Map<string, CustomerInfo>();
      for (const p of profiles) {
        const rows = (p as { product_subscriptions?: SupportSubscriptionRow[] })
          .product_subscriptions;
        const sub = Array.isArray(rows)
          ? rows.find((r) => r.product === product)
          : undefined;
        const trialEndsAt = sub?.trial_ends_at ?? null;
        byEmail.set(p.email, {
          planType: sub?.plan_type ?? p.plan_type ?? null,
          subscriptionStatus:
            sub?.lemonsqueezy_status ?? p.lemonsqueezy_status ?? null,
          periodEnd:
            sub?.lemonsqueezy_current_period_end ??
            p.lemonsqueezy_current_period_end ??
            null,
          cancelAtPeriodEnd: !!(
            sub?.lemonsqueezy_cancel_at_period_end ??
            p.lemonsqueezy_cancel_at_period_end
          ),
          trialEndsAt,
          trialActive:
            !!trialEndsAt && new Date(trialEndsAt).getTime() > Date.now(),
          hasUsedTrial: !!sub?.trial_used_at,
        });
      }

      for (const t of tickets) {
        t.customer = byEmail.get(t.email) ?? null;
      }
    } catch (error) {
      logger.warn(
        "Failed to enrich tickets with customer info",
        "support",
        error,
      );
    }
  }
}

export const supportTicketService = SupportTicketService.getInstance();
